'use client';

import useSWR from 'swr';
import {
  ReactNode,
  createContext,
  useContext,
  useCallback,
  useState,
  useRef,
} from 'react';
import {
  Cart,
  CartLineEdge,
  CartLineUpdateInput,
  CartLineInput,
} from '@/lib/shopify';
import {
  updateCart,
  removeFromCart,
  addToCart as addToCartBrowser,
} from '@/lib/cart.browser';
import {
  calculateTotalQuantity,
  updateCartLineQuantity,
  convertCartItemsToHidden as convertCartItemsToHiddenHelper,
} from '@/lib/cart/helpers';
import { findSubscriptionItemsToRemove } from '@/lib/cart/incompatibleItems';
import {
  trackAddToCart,
  trackCartQuantityChange,
  trackRemoveFromCart,
} from '@/lib/cart/analytics';
import { SerializedCartItem } from '@/hooks/usePlayGeniusCheckout/utils/cartHelpers';
import { PRODUCT_ADDED_COMPONENT_TYPES } from '@/utils/rudderstack';

interface CartContextValue {
  cart: Cart | null;
  isLoading: boolean;
  error: any;
  cartError: string | null;
  updateQuantity: (
    item: SerializedCartItem,
    newQuantity: number,
  ) => Promise<void>;
  removeItem: (item: SerializedCartItem) => Promise<void>;
  addToCart: (
    lines: CartLineInput[],
    quantity: number,
    item: SerializedCartItem,
    selectedVariantIndex: number,
    component: PRODUCT_ADDED_COMPONENT_TYPES,
  ) => Promise<void>;
  convertCartItemsToHidden: () => Promise<void>;
  isMutating: boolean;
  clearCartError: () => void;
}

const CartContext = createContext({} as CartContextValue);

interface CartProviderProps {
  children: ReactNode;
  value?: CartContextValue;
}

export const CartContextProvider: React.FC<CartProviderProps> = ({
  children,
}) => {
  const fetcher = () =>
    fetch('/api/cart', { cache: 'no-cache' }).then((res) => res.json());

  const { data, isLoading, mutate, error } = useSWR<Cart>('/cart', fetcher);
  const [isMutating, setIsMutating] = useState(false);
  const [cartError, setCartError] = useState<string | null>(null);

  // AbortController references for canceling in-progress requests
  const updateQuantityAbortController = useRef<AbortController | null>(null);
  const removeItemAbortController = useRef<AbortController | null>(null);

  const updateQuantity = useCallback(
    async (item: SerializedCartItem, newQuantity: number) => {
      if (!data?.id) return;

      const lineId = item.id;

      const currentLine = data.lines.edges.find(
        (edge: any) => edge.node.id === lineId,
      )?.node;
      if (!currentLine) return;

      if (updateQuantityAbortController.current) {
        updateQuantityAbortController.current.abort();
      }

      updateQuantityAbortController.current = new AbortController();
      const signal = updateQuantityAbortController.current.signal;

      setIsMutating(true);

      const updatedCartLineEdges = data.lines.edges.map((edge: CartLineEdge) =>
        edge.node.id === lineId
          ? updateCartLineQuantity(edge, newQuantity)
          : edge,
      );

      const optimisticData = {
        ...data,
        lines: { edges: updatedCartLineEdges },
        totalQuantity: calculateTotalQuantity(updatedCartLineEdges),
      };

      try {
        const updateInput: CartLineUpdateInput = {
          id: lineId,
          quantity: newQuantity,
        };

        await mutate(
          updateCart([updateInput], {}, signal).then(async (result) => {
            if (!result.ok) {
              const errorResult = result as { ok: false; errorMessage: string };
              throw new Error(
                errorResult.errorMessage || 'Failed to update cart',
              );
            }

            // Return the cart data from the mutation response
            return (result as { ok: true; data: Cart }).data;
          }),
          {
            optimisticData,
            rollbackOnError: true,
            // Disable automatic revalidation since we're returning fresh data
            revalidate: false,
          },
        );
        trackCartQuantityChange({
          item,
          oldQuantity: currentLine.quantity,
          newQuantity,
        });
        setCartError(null);
      } catch (error) {
        // Only handle error if request wasn't aborted
        if (error instanceof Error && error.name !== 'AbortError') {
          setCartError('Item quantity couldn’t be changed. Please try again.');

          console.error('[Cart Error] Update quantity failed:', {
            lineId,
            itemTitle: currentLine.merchandise?.product?.title,
            newQuantity,
            error,
          });

          // Scroll to top to show error
          window?.scrollTo({ top: 0, behavior: 'smooth' });

          throw error;
        }
      } finally {
        setIsMutating(false);
        updateQuantityAbortController.current = null;
      }
    },
    [data, mutate],
  );

  const removeItem = useCallback(
    async (item: SerializedCartItem) => {
      if (!data?.id) return;

      const lineId = item.id;

      const currentLine = data.lines.edges.find(
        (edge: CartLineEdge) => edge.node.id === lineId,
      )?.node;
      if (!currentLine) return;

      if (removeItemAbortController.current) {
        removeItemAbortController.current.abort();
      }

      removeItemAbortController.current = new AbortController();
      const signal = removeItemAbortController.current.signal;

      setIsMutating(true);

      // For subscriptions, we need to remove all related items optimistically
      const isSubscription =
        currentLine.merchandise?.product?.handle === 'subscription-1';
      let filteredCartLineEdges;

      if (isSubscription) {
        // Import the function to get subscription items to remove
        const itemsToRemove = findSubscriptionItemsToRemove(
          lineId,
          data.lines.edges,
        );

        filteredCartLineEdges = data.lines.edges.filter(
          (edge: CartLineEdge) => !itemsToRemove.includes(edge.node.id),
        );
      } else {
        // Regular single item removal
        filteredCartLineEdges = data.lines.edges.filter(
          (edge: CartLineEdge) => edge.node.id !== lineId,
        );
      }

      const optimisticData = {
        ...data,
        lines: { edges: filteredCartLineEdges },
        totalQuantity: calculateTotalQuantity(filteredCartLineEdges),
      };

      try {
        let removePromise: Promise<Cart>;

        if (isSubscription) {
          // Remove subscription and all its addons

          const itemsToRemove = findSubscriptionItemsToRemove(
            lineId,
            data.lines.edges,
          );

          removePromise = removeFromCart(itemsToRemove, {}, signal).then(
            async (result) => {
              if (!result.ok) {
                const errorResult = result as {
                  ok: false;
                  errorMessage: string;
                };
                throw new Error(
                  errorResult.errorMessage ||
                    'Failed to remove subscription and its addons from cart',
                );
              }

              // Return the cart data from the mutation response
              return (result as { ok: true; data: Cart }).data;
            },
          );
        } else {
          // Regular single item removal
          removePromise = removeFromCart([lineId], {}, signal).then(
            async (result) => {
              if (!result.ok) {
                const errorResult = result as {
                  ok: false;
                  errorMessage: string;
                };
                throw new Error(
                  errorResult.errorMessage || 'Failed to remove item from cart',
                );
              }

              // Return the cart data from the mutation response
              return (result as { ok: true; data: Cart }).data;
            },
          );
        }

        await mutate(removePromise, {
          optimisticData,
          rollbackOnError: true,
          // Disable automatic revalidation since we're returning fresh data
          revalidate: false,
        });
        trackRemoveFromCart({
          item,
        });
        setCartError(null);
      } catch (error) {
        // Only handle error if request wasn't aborted
        if (error instanceof Error && error.name !== 'AbortError') {
          setCartError('Item wasn’t removed. Please try again.');

          console.error('[Cart Error] Remove item failed:', {
            lineId,
            itemTitle: currentLine.merchandise?.product?.title,
            error,
          });

          // Scroll to top to show error
          window?.scrollTo({ top: 0, behavior: 'smooth' });

          throw error;
        }
      } finally {
        setIsMutating(false);
        removeItemAbortController.current = null;
      }
    },
    [data, mutate],
  );

  const clearCartError = useCallback(() => {
    setCartError(null);
  }, []);

  const addToCart = useCallback(
    async (
      lines: CartLineInput[],
      quantity: number,
      item: SerializedCartItem,
      selectedVariantIndex: number,
      component: PRODUCT_ADDED_COMPONENT_TYPES,
    ) => {
      if (!data?.id) return;

      setIsMutating(true);

      try {
        const result = await addToCartBrowser(lines, quantity, {
          noredirect: 1,
        });

        if (!result.ok) {
          const errorResult = result as {
            ok: false;
            errorMessage: string;
          };
          const error = new Error(
            errorResult.errorMessage || 'Failed to add item to cart',
          );
          console.error('[Cart] Failed to add item to cart:', error);
          throw error;
        }

        // Update the cart data with the successful result
        await mutate((result as { ok: true; data: Cart }).data, {
          rollbackOnError: true,
          revalidate: true,
        });
        trackAddToCart({
          item,
          quantity,
          selectedVariantIndex,
          component,
        });
      } catch (error) {
        // Only log error if request wasn't aborted
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('[Cart] Failed to add item to cart:', error);
          throw error;
        }
        // For AbortErrors, don't log or re-throw
      } finally {
        setIsMutating(false);
      }
    },
    [data, mutate],
  );

  const convertCartItemsToHidden = useCallback(async () => {
    if (!data) {
      return;
    }

    await convertCartItemsToHiddenHelper(
      data,
      updateCart,
      mutate,
      setIsMutating,
      setCartError,
    );
  }, [data, mutate]);

  const cartContextValue = {
    cart: data || null,
    isLoading,
    error,
    cartError,
    updateQuantity,
    removeItem,
    addToCart,
    convertCartItemsToHidden,
    isMutating,
    clearCartError,
  };

  return (
    <CartContext.Provider value={cartContextValue}>
      {children}
    </CartContext.Provider>
  );
};

const useCartContext = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error('useCartContext must be used within a CartContextProvider');
  }

  return context;
};

export default useCartContext;
